import { Box, Container, Typography, Stack, Card, CardContent, styled } from '@mui/material';
import React, { forwardRef } from 'react';

const FeaturesWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: theme.spacing(10, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Nohemi", sans-serif',
  fontWeight: 600,
  fontSize: '42px',
  lineHeight: 1.2,
  color: '#181D27',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '36px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '28px',
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  lineHeight: 1.6,
  color: '#535862',
  textAlign: 'center',
  marginBottom: theme.spacing(8),
  maxWidth: '600px',
  margin: '0 auto',
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(6),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    marginBottom: theme.spacing(4),
  },
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: '1px solid #F1F3F4',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  },
}));

const FeatureCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const FeatureIcon = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  backgroundColor: '#FEF2F2',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  fontSize: '28px',
}));

const FeatureTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Nohemi", sans-serif',
  fontWeight: 600,
  fontSize: '24px',
  lineHeight: 1.3,
  color: '#181D27',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}));

const FeatureDescription = styled(Typography)(({ theme }) => ({
  fontFamily: '"Inter", sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: 1.6,
  color: '#535862',
  marginBottom: theme.spacing(3),
  flexGrow: 1,
}));

const FeatureImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '200px',
  objectFit: 'contain',
  borderRadius: '12px',
  backgroundColor: '#F9FAFB',
  padding: theme.spacing(2),
  marginTop: 'auto',
}));

const ImagePlaceholder = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '200px',
  backgroundColor: '#F9FAFB',
  borderRadius: '12px',
  border: '2px dashed #D1D5DB',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 'auto',
}));

const PlaceholderText = styled(Typography)(({ theme }) => ({
  color: '#9CA3AF',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  textAlign: 'center',
}));

const PMFeaturesSection = forwardRef<HTMLDivElement>((props, ref) => {
  const features = [
    {
      icon: '📄',
      title: 'Intelligent Document Processing',
      description:
        'Upload documents or forward emails to your property inbox. Our AI automatically understands and categorizes every document, from mortgage statements to home improvement receipts, making them instantly searchable and actionable.',
      imagePath: 'assets/images/document_input.svg',
    },
    {
      icon: '📅',
      title: 'Smart Property Calendar',
      description:
        "Stay on top of your property's critical dates. Our AI automatically tracks and reminds you about important deadlines - mortgage payments, property tax due dates, insurance renewals, and maintenance schedules - ensuring you never miss a payment or important task.",
      imagePath: 'assets/images/calendar.svg',
    },
  ];

  return (
    <FeaturesWrapper ref={ref}>
      <Container maxWidth="lg">
        <SectionTitle>Powerful Features for Modern Real Property Users</SectionTitle>

        <SectionSubtitle>
          Discover how AI can transform your property workflow and give you complete control over
          your property's knowledge.
        </SectionSubtitle>

        <Stack spacing={4} direction={{ xs: 'column', md: 'row' }} sx={{ mt: 4 }}>
          {features.map(feature => (
            <Box key={feature.title} sx={{ flex: 1 }}>
              <FeatureCard>
                <FeatureCardContent>
                  <FeatureIcon>{feature.icon}</FeatureIcon>

                  <FeatureTitle>{feature.title}</FeatureTitle>

                  <FeatureDescription>{feature.description}</FeatureDescription>

                  <FeatureImage
                    src={feature.imagePath}
                    alt={feature.title}
                    onError={e => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const placeholder =
                        target.parentElement?.querySelector('.fallback-placeholder');
                      if (placeholder) {
                        (placeholder as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                  <ImagePlaceholder className="fallback-placeholder" sx={{ display: 'none' }}>
                    <PlaceholderText>{feature.imagePath}</PlaceholderText>
                  </ImagePlaceholder>
                </FeatureCardContent>
              </FeatureCard>
            </Box>
          ))}
        </Stack>
      </Container>
    </FeaturesWrapper>
  );
});

PMFeaturesSection.displayName = 'PMFeaturesSection';

export default PMFeaturesSection;
